/**
 * Location Switcher Component
 *
 * Dropdown for multi-location retail partners to switch between locations.
 * Shows current location and allows switching to other accessible locations.
 */

import React, { useState, useRef, useEffect } from 'react';
import { usePortalLocation } from '../../contexts/PortalLocationContext';

// Icons (inline SVGs to avoid dependency issues)
const MapPinIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export const LocationSwitcher: React.FC = () => {
  const {
    locations,
    selectedLocation,
    isMultiLocation,
    isOrgOwner,
    switchLocation,
    retailOrg,
    loading,
  } = usePortalLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSwitch = async (locationId: number | null) => {
    try {
      setSwitching(true);
      await switchLocation(locationId);
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to switch location:', err);
    } finally {
      setSwitching(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-gray-500">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  // No retail org or locations - don't render anything
  if (!retailOrg && locations.length === 0) {
    return null;
  }

  // Single location - just display it, no dropdown
  if (!isMultiLocation) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-gray-700">
        <span className="text-green-600">
          <MapPinIcon />
        </span>
        <span className="font-medium">
          {selectedLocation?.full_display_name || selectedLocation?.name || retailOrg?.name}
        </span>
      </div>
    );
  }

  // Multi-location - show dropdown
  const displayText = selectedLocation?.short_name || selectedLocation?.name || 'All Locations';
  const totalMembers = locations.reduce((sum, l) => sum + (l.member_count || 0), 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-green-600">
          <BuildingIcon />
        </span>
        <span className="font-medium text-gray-800">{displayText}</span>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="listbox"
        >
          {/* Header */}
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Switch Location
            </div>
          </div>

          {/* "All Locations" option - only for org owners */}
          {isOrgOwner && (
            <>
              <button
                onClick={() => handleSwitch(null)}
                disabled={switching}
                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
                  !selectedLocation ? 'bg-green-50' : ''
                }`}
                role="option"
                aria-selected={!selectedLocation}
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">
                    <BuildingIcon />
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">All Locations</div>
                    <div className="text-sm text-gray-500">
                      {totalMembers.toLocaleString()} total members • {locations.length} locations
                    </div>
                  </div>
                </div>
                {!selectedLocation && <CheckIcon />}
              </button>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}

          {/* Individual locations */}
          <div className="max-h-64 overflow-y-auto">
            {locations.map(location => (
              <button
                key={location.id}
                onClick={() => handleSwitch(location.id)}
                disabled={switching}
                className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
                  selectedLocation?.id === location.id ? 'bg-green-50' : ''
                }`}
                role="option"
                aria-selected={selectedLocation?.id === location.id}
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">
                    <MapPinIcon />
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">
                      {location.short_name || location.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {location.city}, {location.state} • {(location.member_count || 0).toLocaleString()} members
                    </div>
                  </div>
                </div>
                {selectedLocation?.id === location.id && <CheckIcon />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;
